import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import config from './evaluation.config.json';
import { handleSubmitEvaluation } from '@/api/grt/evaluation';
import ToastUtils from '@/utils/ToastUtils';
import StarRateRow from './components/StarRateRow';
import TextQuestionRow from './components/TextQuestionRow';
import s from './style.module.scss';
// 弹窗标题装饰图（"我要评价"艺术字）
const titleIcon = require('@/assets/images/simulation/evaluationTool/title_icon.webp');

// 评价场景语义 key（对应 evaluation.config.json 的 scenes key）
export const SCENE_KEY = {
    caseSimulation: 'caseSimulation' // 案例仿真（scene=3）
};
// 案例仿真评价场景码（查询评价状态接口入参用）
export const CASE_SIMULATION_SCENE = 3;

// 开放题固定约定码：提交报文中映射到后端 suggestion 字段（非 dimension_scores）
const SUGGESTION_CODE = 'SUGGESTION';

/**
 * 案例仿真评价弹窗：题目渲染/必选校验/提交均由题库 JSON（evaluation.config.json）驱动。
 * @param {boolean}  open       是否展示
 * @param {string}   scene      场景语义 key，见 SCENE_KEY
 * @param {object}   params     业务身份参数（组装提交报文）：{ business_id, resource_id, class_id?, template_id? }
 * @param {function} onClose    关闭回调（暂不评价/提交成功后关闭）
 * @param {function} onSuccess  提交成功回调（父级切换已评价态）
 */
const EvaluationModal = (props) => {
    const { open, scene, params = {}, onClose, onSuccess } = props,
        sceneConfig = config.scenes[scene] || {},
        questions = sceneConfig.questions || [],
        [answers, setAnswers] = useState({}),
        [submitting, setSubmitting] = useState(false);

    // 必答完成度：所有非文本题均已选择（题目量级小，普通派生值即可，无需 useMemo）
    const allRequiredAnswered = questions.filter((q) => q.type !== 'text').every((q) => answers[q.code] !== undefined);

    const onAnswer = (code, value) => {
        setAnswers((prev) => ({ ...prev, [code]: value }));
    };

    // 每次打开：重置表单 + 上报弹窗标记（弹出即标记，无论后续跳过/提交）
    useEffect(() => {
        if (open) {
            setAnswers({});
            setSubmitting(false);
        }
    }, [open]);

    const onSubmit = async () => {
        if (submitting) return;
        // 未完成所有题目作答 → toast"请完成所有评价项"，弹窗保持
        if (!allRequiredAnswered) {
            ToastUtils.showToast('请完成所有评价项');
            return;
        }
        setSubmitting(true);
        try {
            const dimension_scores = questions
                .filter((q) => q.type !== 'text')
                .map((q) => ({ dimension_code: q.code, score: answers[q.code] }));
            const requestBody = {
                scene: sceneConfig.scene,
                ...params,
                dimension_scores
            };
            const suggestion = answers[SUGGESTION_CODE];
            if (suggestion) {
                requestBody.suggestion = suggestion;
            }
            const res = await handleSubmitEvaluation(requestBody);
            if (res?.code === '00000') {
                ToastUtils.showToast('感谢您的评价！');
                onSuccess?.();
                onClose?.();
            } else {
                ToastUtils.showAbnormalToast(res?.msg || '评价提交失败，请重试');
            }
        } catch (error) {
            console.error(error, '提交评价失败');
            ToastUtils.showAbnormalToast('评价提交失败，请重试');
        } finally {
            setSubmitting(false);
        }
    };

    const renderQuestion = (question) => {
        const commonProps = {
            question,
            value: answers[question.code],
            onChange: onAnswer
        };
        switch (question.type) {
            case 'star':
                return <StarRateRow key={question.code} {...commonProps} />;
            case 'text':
                return <TextQuestionRow key={question.code} {...commonProps} />;
            default:
                return null;
        }
    };

    const canSubmit = allRequiredAnswered && !submitting;

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            maskClosable={false}
            destroyOnClose={true}
            width={560}
            centered={true}
            wrapClassName={s['evaluation-modal-wrap']}
        >
            <div className={s['evaluation-tool-modal']}>
                <div className={s['modal-header']}>
                    <div className={s['modal-title-wrap']}>
                        <div className={s['left']}>
                            <img className={s['modal-title']} src={titleIcon} alt={sceneConfig.title} />
                        </div>
                        <div className={s['right']}>
                            <span className={s['skip-btn']} onClick={onClose}>
                                {sceneConfig.skipText}
                            </span>
                        </div>
                    </div>
                    <div className={s['modal-tip']}>{sceneConfig.header}</div>
                </div>
                <div className={s['modal-content']}>
                    <div className={s['question-list']}>{questions.map(renderQuestion)}</div>
                    <div className={s['modal-footer']}>
                        {/* 按钮始终可点：未完成时点击 toast"请完成所有评价项"，置灰仅作视觉提示 */}
                        <button
                            className={`${s['submit-btn']} ${canSubmit ? '' : s['submit-btn-disabled']}`}
                            disabled={submitting}
                            onClick={onSubmit}
                        >
                            {submitting ? '提交中…' : sceneConfig.submitText}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default EvaluationModal;
